import { IconSparkles } from 'featherico';
import type Quill from 'quill';
import type DeltaStatic from 'quill-delta';
import { useEffect, useState } from 'react';
import { WritingAssistantContext, WritingAssistantPromptType } from '../../../../types/WritingAssistant';
import { Dropdown, Option } from '../../components/Dropdown';
import ToolbarButton from '../../components/RichTextArea/Toolbar/button';
import SparkPosition from '../../components/Spark/SparkPosition';
import SuspenseWithErrorModal from '../../components/SuspenseWithErrorModal';
import { isFeatureEnabled } from '../../services/Features';
import { trackEvent } from '../../services/Tracking';
import styles from './style.m.less';
import WritingAssistantFloating from './WritingAssistantFloatingLazy';
import WritingAssistantModal from './WritingAssistantModalLazy';

interface Props {
    quill?: Quill;
    getText: () => string;
    setHtml: (text: string) => DeltaStatic;
    context: WritingAssistantContext;
}

let sparkAlreadyShown = false;

export default function WritingAssistantToolbarButton({ quill, getText, setHtml, context }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [contentForPrompt, setContentForPrompt] = useState(getText() ?? '');
    const [showSpark, setShowSpark] = useState(false);
    const [promptType, setPromptType] = useState<WritingAssistantPromptType>(context.constructiveType);

    const handleClick = (type: WritingAssistantPromptType) => {
        trackEvent({
            category: 'AI_WRITING_ASSISTANT',
            label: 'toolbar button',
            action: `click ${type}`,
        });
        setPromptType(type);
        setContentForPrompt(getText());
        setIsOpen(true);
    };
    const handleClose = () => {
        setIsOpen(false);
    };

    useEffect(() => {
        if (!sparkAlreadyShown) {
            sparkAlreadyShown = true;
            setShowSpark(true);

            return () => {
                sparkAlreadyShown = false;
            };
        }
    }, []);

    return (
        <>
            {showSpark && (
                <SparkPosition x={-18} y={10} zIndex={2} story="AI_ASSISTANT" type="toolbarButton"></SparkPosition>
            )}
            {isFeatureEnabled('FEATURE_aiWritingAssistantOverhaul') ? (
                <>
                    <Dropdown
                        kind="TRANSPARENT"
                        showChevron={false}
                        buttonClass={styles.dropdownButton}
                        label={
                            <>
                                <IconSparkles /> AI
                            </>
                        }
                    >
                        <Option onClick={() => handleClick(context.constructiveType)}>Refine</Option>
                        <Option onClick={() => handleClick('COACH')}>Coach</Option>
                    </Dropdown>
                    <SuspenseWithErrorModal enableErrorModal={isOpen}>
                        {isOpen && (
                            <WritingAssistantFloating
                                isOpen={isOpen}
                                onClose={handleClose}
                                content={contentForPrompt}
                                setHtml={setHtml}
                                context={context}
                                promptType={promptType}
                                quill={quill}
                            />
                        )}
                    </SuspenseWithErrorModal>
                </>
            ) : (
                <>
                    <ToolbarButton onClick={() => handleClick(context.constructiveType)} title="AI Writing Assistant">
                        <IconSparkles /> AI
                    </ToolbarButton>
                    <SuspenseWithErrorModal enableErrorModal={isOpen}>
                        <WritingAssistantModal
                            isOpen={isOpen}
                            onClose={handleClose}
                            content={contentForPrompt}
                            setHtml={setHtml}
                            context={context}
                            promptType={promptType}
                        />
                    </SuspenseWithErrorModal>
                </>
            )}
        </>
    );
}
